import { GL_ROLES } from '../types';
import type { GlAccount } from '../types';
import { chartWithMappedCodes, customRoleKey } from './glChart';

/**
 * G/L Mappings as ONE list (ruled by Christian, 2026-10-28): the client's chart, Xero-synced or typed, where each account
 * says what it is USED AS: a template role, a custom line, or nothing. The separate mapping-row form is gone.
 *
 * Nothing about the stored map changes: `roles` (role → code), `rowLabels` (a custom line's label) and `accounts` are
 * saved exactly as before, so the server, its validation and every template read the same record. These helpers are
 * the view over it. A role still has ONE account; an account may serve several roles.
 */

/** Kept in the saved map (old journals are graded on it), never shown or offered: retired 2026-10-27. */
export const RETIRED_ROLES: ReadonlySet<string> = new Set(['salesTaxClearing']);
const CANONICAL: ReadonlySet<string> = new Set<string>(GL_ROLES);

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The list's rows: the chart, plus every code a VISIBLE mapping names that it lacks (the migration of the old rows).
 * A code only the retired role names is not made a row: it stays in the saved map, and nothing else uses it.
 */
export function oneListAccounts(accounts: readonly GlAccount[], roles: Readonly<Record<string, string>>): GlAccount[] {
  return chartWithMappedCodes(
    accounts,
    Object.entries(roles).filter(([role]) => !RETIRED_ROLES.has(role)).map(([, code]) => code),
  );
}

/** What this account is used as: the visible roles mapped to its code. */
export function usedAs(roles: Readonly<Record<string, string>>, code: string): string[] {
  return Object.entries(roles)
    .filter(([role, mapped]) => !RETIRED_ROLES.has(role) && mapped.trim() !== '' && same(mapped, code))
    .map(([role]) => role);
}

/**
 * Set what an account is used as. A role chosen here leaves the account it was on (a role has one account); a role
 * no longer chosen here is unmapped. Roles on other accounts, and the retired role, are untouched.
 */
export function setUsedAs(
  roles: Readonly<Record<string, string>>,
  code: string,
  chosen: readonly string[],
): { roles: Record<string, string>; moved: { role: string; from: string }[] } {
  const next: Record<string, string> = { ...roles };
  const moved: { role: string; from: string }[] = [];
  for (const role of usedAs(roles, code)) if (!chosen.includes(role)) delete next[role];
  for (const role of chosen) {
    const was = roles[role];
    if (was && was.trim() !== '' && !same(was, code)) moved.push({ role, from: was.trim() });
    next[role] = code.trim();
  }
  return { roles: next, moved };
}

export interface UsedAsOption {
  key: string;
  /** A line the client defined (or a role PTC never named); its label is its own, not a translation. */
  custom: boolean;
  label?: string;
}

/** Everything an account can be used as: the template roles in their documented order, then the client's own lines. */
export function usedAsOptions(
  roles: Readonly<Record<string, string>>,
  rowLabels: Readonly<Record<string, string>>,
): UsedAsOption[] {
  const custom = [...new Set([...Object.keys(roles), ...Object.keys(rowLabels)])]
    .filter((key) => !CANONICAL.has(key) && !RETIRED_ROLES.has(key))
    .sort((a, b) => (rowLabels[a] ?? a).localeCompare(rowLabels[b] ?? b));
  return [
    ...GL_ROLES.map((key) => ({ key, custom: false })),
    ...custom.map((key) => ({ key, custom: true, label: rowLabels[key] ?? key })),
  ];
}

/** The roles and lines no account is used as yet: said under the list, so a missing mapping is not invisible. */
export function unusedLines(
  roles: Readonly<Record<string, string>>,
  rowLabels: Readonly<Record<string, string>>,
): UsedAsOption[] {
  return usedAsOptions(roles, rowLabels).filter((o) => !(roles[o.key] ?? '').trim());
}

/**
 * A line's name as compared for sameness (wave 4 §1.2): case, dashes and spacing do not make two names different.
 * The server's `GlChartRules.SameName`, which refuses a save with two lines of the same name (`gl-line-name-duplicate`).
 */
export const sameName = (label: string): string =>
  label.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/**
 * The key for a line called `label`: the key of the client's line already called that, else a new one (wave 4 §1.2.1).
 * Minting a second key for a name the client already had is how Moretti's map came to hold every line twice.
 */
export function lineKeyFor(label: string, rowLabels: Readonly<Record<string, string>>, taken: Iterable<string>): { key: string; existing: boolean } {
  const wanted = sameName(label);
  const found = Object.entries(rowLabels).find(([, l]) => sameName(l) === wanted);
  return found ? { key: found[0], existing: true } : { key: customRoleKey(label, taken), existing: false };
}

export type UseGroup = 'own' | 'standard' | 'elsewhere';

export interface GroupedUse extends UsedAsOption {
  group: UseGroup;
  /** The account it is on now, for a use on another account. */
  elsewhere: string | null;
}

/**
 * The Used-as picker's list, grouped (wave 4 §1b): this client's lines first, then the standard roles, then, at the bottom,
 * every use already on ANOTHER account (dimmed, with where it is now; never hidden). A use this account has stays in its group.
 */
export function groupedUses(
  roles: Readonly<Record<string, string>>,
  rowLabels: Readonly<Record<string, string>>,
  accountCode: string,
): GroupedUse[] {
  const here = (c: string) => c.trim().toLowerCase() === accountCode.trim().toLowerCase();
  const all = usedAsOptions(roles, rowLabels).map((o): GroupedUse => {
    const on = (roles[o.key] ?? '').trim();
    const elsewhere = on !== '' && (accountCode.trim() === '' || !here(on)) ? on : null;
    return { ...o, group: elsewhere ? 'elsewhere' : o.custom ? 'own' : 'standard', elsewhere };
  });
  const order: UseGroup[] = ['own', 'standard', 'elsewhere'];
  return order.flatMap((g) => all.filter((o) => o.group === g));
}

/** The use whose name is exactly `name` (by `sameName`), for "typing a name that matches a known role picks it" (§2). */
export function findUseNamed(name: string, options: readonly UsedAsOption[], labelOf: (o: UsedAsOption) => string): string | null {
  const wanted = sameName(name);
  if (!wanted) return null;
  return options.find((o) => sameName(labelOf(o)) === wanted)?.key ?? null;
}
